import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useAgents } from "../../../hooks/useAgents";
import Loading from "../../Loading";
import bulkPageReducer from "../../../reducers/bulkPageReducer";
import { setPageError } from "../../../reducers/rootReducer";
import { isEmptyObject, shuffleArray } from "../../../support/helpers";
import StatesAgentsDeck from "./AgentsDeck";
import LineBehind from "../../LineBehind";
import AgentSelector from "./AgentSelector";
import StateSelector from "./StateSelector";
import NicheSelector from "./NicheSelector";
import MapSelector from "./MapSelector";
import NoAgents from "./NoAgents";
import PageTitle from "../PageTitle";
import { useQueryParams } from "use-query-params";
import { FilterModel } from "./filterModel";
import { setPageHeaderInfo } from "../../../reducers/pagesReducer";

const Agents = () => {
    const dispatch = useDispatch();
    const brokerAgents = useSelector((state) => state.pages.brokerAgents);
    const { states, nicheDomain2ndLevel } = useSelector(
        (state) => state.root.styling
    );
    const history = useHistory();

    const hasAgents =
        !isEmptyObject(brokerAgents?.agentsInfo) &&
        !isEmptyObject(brokerAgents?.agentsInfo?.brokerAgentsInfo);
    const { execute, isLoading, error } = useAgents();
    const {
        brokerAgentsInfo,
        brokerAgentsNicheItemsServicedInfo,
        brokerAgentsStatesServicedInfo,
    } = brokerAgents?.agentsInfo || {};
    const [filterItems, setFilterItems] = useQueryParams(FilterModel);
    const [shuffledAgents, setShuffledAgents] = useState([]);

    useEffect(() => {
        if (brokerAgents) {
            dispatch(setPageHeaderInfo(brokerAgents?.pageHeaderInfo));
        }
    }, [brokerAgents, dispatch]);

    useEffect(() => {
        if (brokerAgentsInfo) {
            // Agents have a sort order that is set in the DB. This will loop through the agents
            // and seperate them into the approprite 'band', shuffle each and then combine into the
            // final random list by band.
            const sortByBand = {};
            const shffledAgentsByBand = [];
            brokerAgentsInfo.forEach((agent) => {
                const { randomSortBand = 1 } = agent;
                sortByBand[randomSortBand] = [
                    ...(sortByBand[randomSortBand] || []),
                    agent,
                ];
            });
            Object.values(sortByBand).forEach((band) =>
                shffledAgentsByBand.push(...shuffleArray(band))
            );
            setShuffledAgents(shffledAgentsByBand);
        }
    }, [brokerAgentsInfo]);

    useEffect(() => {
        if (!hasAgents) {
            const fetchData = async () => {
                bulkPageReducer(dispatch, {
                    pageType: "agents",
                    ...(await execute()),
                });
            };
            fetchData();
        }
        document.title =
            brokerAgents?.pageHeaderInfo?.title ||
            `Find an Agent - ${nicheDomain2ndLevel}.com`;
        // eslint-disable-next-line
    }, [brokerAgents]);

    useEffect(() => {
        if (error) {
            dispatch(
                setPageError({
                    page: "States Agents",
                    error: "Error loading agents page.",
                })
            );
        }
    }, [dispatch, error]);

    const buildFilterItems = (type, value) => {
        setFilterItems((filters) => ({ ...filters, [type]: value }));
    };

    const handleAgentClick = (agentId) => {
        if (agentId) {
            const agent = brokerAgentsInfo.find(
                (agent) => agent.id === agentId
            );
            const nicheItemId =
                filterItems?.nicheItemId ||
                agent?.brokerAgentNicheItemsPrimary[0] ||
                agent.brokerAgentNicheItems[0];
            const stateId = filterItems?.state || agent.brokerAgentStates[0];
            const { nicheItemPath } = brokerAgentsNicheItemsServicedInfo.find(
                (item) => item.nicheItemId === nicheItemId
            );
            history.push({
                pathname: `/${nicheItemPath}/${agent.uRI4Agent}`,
                state: { agentId: agent.id, stateId, nicheItemId },
            });
        }
    };

    return (
        <>
            {isLoading && <Loading />}
            {!isLoading && (
                <div className="w-100" data-testid="agents">
                    <div className="container">
                        <PageTitle
                            title="Find an agent"
                            titleClass="my-3"
                            data-test="title"
                        />
                        {!hasAgents ? (
                            <NoAgents />
                        ) : (
                            <>
                                <div className="row g-0">
                                    <div className="col-md-5 g-0">
                                        <div className="h-100 d-flex flex-column align-items-center justify-content-around">
                                            <NicheSelector
                                                label="Filter by Market"
                                                nicheItems={
                                                    brokerAgentsNicheItemsServicedInfo
                                                }
                                            />
                                            <LineBehind
                                                className="w-100 text-center my-2"
                                                textClassName="bg-light px-2"
                                            />
                                            <AgentSelector
                                                label="Select by Agent Name"
                                                agents={shuffledAgents}
                                                filterItems={filterItems}
                                                handleClick={handleAgentClick}
                                            />
                                            <LineBehind
                                                className="w-100 text-center my-2"
                                                textClassName="bg-light px-2"
                                            />
                                            <StateSelector
                                                label="Filter by State"
                                                statesServiced={
                                                    brokerAgentsStatesServicedInfo
                                                }
                                            />
                                        </div>
                                    </div>
                                    <div className="px-2 col-md-7 d-none d-md-block">
                                        <MapSelector
                                            states={Object.values(states)}
                                            statesServiced={
                                                brokerAgentsStatesServicedInfo
                                            }
                                        />
                                    </div>
                                </div>
                                <StatesAgentsDeck
                                    agents={shuffledAgents}
                                    nicheItems={
                                        brokerAgentsNicheItemsServicedInfo
                                    }
                                    statesServiced={
                                        brokerAgentsStatesServicedInfo
                                    }
                                    handleClick={handleAgentClick}
                                />
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default Agents;
